// Helpers for HudPanel — kept in a sibling file so HudPanel.ts can stay
// under the 600-line per-file cap (Rule 15) while the Command Card
// machinery grows. Helpers are stateless (no module-level vars). They do
// touch the DOM: factories call `document.createElement`, and `ensureDiv`
// looks up by id and appends to `document.body` for the HUD root.

import type {
  Building,
  Core,
  MatchState,
  PermanentBuildingKind,
  PlayerId,
  Squad,
  SquadArchetype,
} from '@signalclash/shared';
import {
  BUILDINGS,
  MAX_HUBS_PER_PLAYER,
  PERMANENT_BUILDING_KINDS,
  abilityForArchetype,
  secondaryAbilityForArchetype,
} from '@signalclash/shared';

// ── DOM helpers (top resource line, root container) ───────────────────────

export function makeStat(
  label: string,
  initial: string,
  valueClass: string,
): { row: HTMLElement; value: HTMLElement } {
  const row = document.createElement('div');
  row.className = 'hud-stat';
  const labelEl = document.createElement('span');
  labelEl.className = 'hud-stat__label';
  labelEl.textContent = label;
  const value = document.createElement('span');
  value.className = `hud-stat__value ${valueClass}`;
  value.textContent = initial;
  row.appendChild(labelEl);
  row.appendChild(value);
  return { row, value };
}

export function ensureDiv(id: string, className: string): HTMLDivElement {
  const existing = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = id;
  div.className = className;
  document.body.appendChild(div);
  return div;
}

export function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

export function formatClock(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = Math.max(0, Math.floor(totalSec - m * 60));
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── V4 Command Card glyphs + types ────────────────────────────────────────
// Glyphs are deliberately common-Unicode so they render uniformly across
// desktop and mobile system fonts. The portrait + production tiles reuse
// the same glyph table so the player learns one visual vocabulary.

export const ARCHETYPE_GLYPH: Record<SquadArchetype, string> = {
  recon: '◇',
  assault: '⚔',
  engineer: '⚒',
  shield: '◈',
};

// Short role taglines surfaced in the squad command card. One short phrase per
// archetype — the goal is "new player understands what this unit is for in
// under one second" without reading stats.
export const ARCHETYPE_TAGLINE: Record<SquadArchetype, string> = {
  recon: 'Fast scout · paints targets',
  assault: 'Heavy DPS · main attacker',
  engineer: 'Captures faster · builds turrets',
  shield: 'Tank · projects shield',
};

// V1.7 (2.11) — full-sentence tooltip for each production button. Surfaces
// the *purpose* of each archetype at the moment of decision (hovering the
// build tile), not just after selecting an already-built squad. Closes the
// "I don't know why I'd ever pick Engineer" comprehension gap from the
// playtest report. Touch profile suppresses tooltips entirely — mobile
// players get the same info from the pre-match IntroOverlay + the in-card
// tagline once a squad is selected.
export const ARCHETYPE_BUILD_TOOLTIP: Record<SquadArchetype, string> = {
  recon:    'Cheap, fast scout. Paints enemies to amplify your damage and grants vision through fog. Best for early relay sprints.',
  assault:  'Heavy DPS — your main attacker. Pair with a Recon (marked targets take more damage) once you can afford both.',
  engineer: 'Captures relays 50% faster and builds Mini-Turrets on signal links. Pick one early to grab the map and forward-fortify.',
  shield:   'Tank with a projected shield bubble that protects nearby squads. Build before pushing the enemy Core.',
};

// V1.7 (2.11) — same idea for the Hub/Turret build tiles. Hub answers
// "what does this actually give me?" — each Hub adds a parallel squad
// production queue, so 1 Hub gives 2 queues and 2 Hubs gives 3 queues; the
// Core keeps producing throughout. Turret answers "where does this go?" —
// static defense on a Core slot.
export const BUILD_KIND_TOOLTIP: Record<PermanentBuildingKind, string> = {
  production_hub: 'Adds a parallel squad production queue. Your Core keeps producing too, so 1 Hub = 2 queues, 2 Hubs = 3 queues. Place forward for shorter spawn-to-front travel.',
  turret:         'Static defense, auto-fires at the nearest enemy in range (400 px). Place on a Core slot to guard the Core itself or to anchor a chokepoint.',
};

// V1.7 (3.18) — short "what does this do" snippet rendered under cost/time
// on each build button. Cost+time alone doesn't answer "Hub vs Turret, which
// one?"; the effect line does. Tooltip still owns the full explanation.
export const BUILD_KIND_EFFECT: Record<PermanentBuildingKind, string> = {
  production_hub: '+1 Queue',
  turret:         '30 DPS · 400px',
};

// V1.7 (3.18) — surfaces the hidden third building type to newcomers. The
// player-buildable list is just Hub + Turret, but Mini-Turrets ARE in the
// game (Engineer Q deploys one on contact with a relay). Without this hint
// the third option lives behind picking the right squad, picking the right
// ability, and clicking at the right spot — far below the visibility floor
// for a casual playtest. Rendered as a footer under the Build row.
export const BUILD_PANEL_FOOTER_HINT =
  'Engineer ✦ also deploys forward Mini-Turrets at relays — three building types in all.';

export const BUILD_KIND_GLYPH: Record<PermanentBuildingKind, string> = {
  production_hub: '⊞',
  turret: '⛉',
};

// V1.7 — narrowed from 4 verbs to 2: the player-facing Attack-Move and Stop
// commands were removed (squads auto-engage in-range enemies via CombatSystem
// regardless of order kind, and Stop was redundant since issuing a Move at
// the squad's current position has the same effect). Only the two
// archetype-driven ability slots remain in the squad command card.
export type CmdKey = 'ability' | 'ability2';

export interface CmdCell {
  btn: HTMLButtonElement;
  icon: HTMLElement;
  label: HTMLElement;
  hotkey: HTMLElement;
  cd: HTMLElement;
}

export interface CmdState {
  label: string;
  enabled: boolean;
  armed: boolean;
  readyPct: number;
  hotkey: string;
  ready: boolean;
  hidden?: boolean;
}

interface CmdSpec {
  key: CmdKey;
  kind: 'ability';
  icon: string;
  defaultLabel: string;
  defaultHotkey: string;
  ariaLabel: string;
}

export interface ProdButton {
  el: HTMLButtonElement;
  meta: HTMLElement;
}

export interface BuildButton {
  el: HTMLButtonElement;
  meta: HTMLElement;
}

export const CMD_CELL_SPECS: readonly CmdSpec[] = [
  { key: 'ability',  kind: 'ability', icon: '✦', defaultLabel: 'Ability', defaultHotkey: 'Q', ariaLabel: 'Primary ability' },
  { key: 'ability2', kind: 'ability', icon: '✧', defaultLabel: 'Ability', defaultHotkey: 'E', ariaLabel: 'Secondary ability' },
];

export function makeCmdCell(spec: CmdSpec): CmdCell {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hud-context__cmd';
  btn.dataset.kind = spec.kind;
  btn.dataset.cmd = spec.key;
  btn.setAttribute('aria-label', spec.ariaLabel);
  const icon = document.createElement('span');
  icon.className = 'hud-context__cmd-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = spec.icon;
  const label = document.createElement('span');
  label.className = 'hud-context__cmd-label';
  label.textContent = spec.defaultLabel;
  const hotkey = document.createElement('span');
  hotkey.className = 'hud-context__cmd-key';
  hotkey.textContent = spec.defaultHotkey;
  const cd = document.createElement('span');
  cd.className = 'hud-context__cmd-cd';
  btn.appendChild(icon);
  btn.appendChild(label);
  btn.appendChild(hotkey);
  btn.appendChild(cd);
  return { btn, icon, label, hotkey, cd };
}

export function applyCmd(cell: CmdCell, s: CmdState): void {
  cell.btn.style.display = s.hidden === true ? 'none' : '';
  cell.btn.disabled = !s.enabled;
  cell.btn.dataset.armed = String(s.armed);
  cell.btn.dataset.ready = String(s.ready);
  cell.label.textContent = s.label;
  cell.hotkey.textContent = s.hotkey;
  const drained = Math.max(0, Math.min(1, 1 - s.readyPct));
  cell.cd.style.width = `${(drained * 100).toFixed(1)}%`;
  cell.cd.style.display = drained > 0 ? '' : 'none';
}

export function labelForBuildKind(kind: PermanentBuildingKind): string {
  return kind === 'production_hub' ? 'Production Hub' : 'Turret';
}

export function computeSquadCmdStates(
  squad: Squad,
  viewerId: PlayerId,
  armed: 'idle' | 'mark-target' | 'cut-link' | 'build',
): Map<CmdKey, CmdState> {
  const ownerOk = squad.ownerId === viewerId;
  const out = new Map<CmdKey, CmdState>();
  const primary = abilityForArchetype(squad.archetype);
  const primaryCd = Math.max(0, squad.abilityCooldown);
  // Align with InputRouter.onAbilityDown: it rejects when `cooldown > 0`, so
  // the click is only safe when the cooldown has fully drained to zero.
  // Using `<= SECONDS_PER_TICK` instead would surface a "Ready" button for
  // ~one tick where the click silently no-ops.
  const primaryReady = primaryCd <= 0;
  const primaryPct = primary.cooldownSec > 0
    ? Math.max(0, Math.min(1, 1 - primaryCd / primary.cooldownSec)) : 1;
  out.set('ability', {
    label: primary.name,
    enabled: ownerOk && primaryReady && squad.channelingAbility === null,
    armed: armed === 'mark-target',
    readyPct: primaryPct, hotkey: primary.hotkey, ready: primaryReady && ownerOk,
  });
  const secondary = secondaryAbilityForArchetype(squad.archetype);
  if (secondary === null) {
    out.set('ability2', { label: '—', enabled: false, armed: false, readyPct: 0, hotkey: 'E', ready: false, hidden: true });
    return out;
  }
  const secCd = Math.max(0, squad.secondaryAbilityCooldown);
  // Same alignment as primary — InputRouter.onSecondaryAbilityDown rejects
  // when `cooldown > 0` AND when `channelingAbility !== null`.
  const secReady = secCd <= 0;
  const secPct = secondary.cooldownSec > 0
    ? Math.max(0, Math.min(1, 1 - secCd / secondary.cooldownSec)) : 1;
  out.set('ability2', {
    label: secondary.name,
    enabled: ownerOk && secReady && squad.channelingAbility === null,
    armed: armed === 'cut-link',
    readyPct: secPct, hotkey: secondary.hotkey, ready: secReady && ownerOk, hidden: false,
  });
  return out;
}

// ── Build affordability ────────────────────────────────────────────────────
// Mirrors BuildPanel.refreshButtons so the in-card Build row in the HUD
// stays in sync with the legacy placement indicator. Returned object is
// applied to each per-kind button by the caller.

export interface BuildKindState {
  disabled: boolean;
  title: string;
}

export function computeBuildKindStates(
  state: MatchState,
  playerId: PlayerId,
): Map<PermanentBuildingKind, BuildKindState> {
  const player = state.players[playerId];
  let hubsOwned = 0;
  let slotsOpen = 0;
  let coreId: number | null = null;
  for (const c of Object.values<Core>(state.cores)) {
    if (c.ownerId === playerId) coreId = c.id;
  }
  if (coreId !== null) {
    const occupied = new Set<number>();
    for (const b of Object.values<Building>(state.buildings)) {
      if (b.kind === 'mini_turret') continue;
      if (b.coreId === coreId) occupied.add(b.slotIndex);
      if (b.ownerId === playerId && b.kind === 'production_hub') hubsOwned += 1;
    }
    for (let i = 0; i < 6; i++) if (!occupied.has(i)) slotsOpen += 1;
  }
  const out = new Map<PermanentBuildingKind, BuildKindState>();
  // Iterate the shared list so adding a new permanent building kind in the
  // sim package automatically surfaces it here (no drift between HUD and
  // the shared model).
  for (const kind of PERMANENT_BUILDING_KINDS) {
    const def = BUILDINGS[kind];
    const cantAfford = player.signal < def.cost;
    const noSlot = slotsOpen === 0;
    const hubLimit = kind === 'production_hub' && hubsOwned >= MAX_HUBS_PER_PLAYER;
    const disabled = cantAfford || noSlot || hubLimit;
    const title = cantAfford
      ? `Need ${def.cost} Signal`
      : hubLimit
        ? `Already at ${MAX_HUBS_PER_PLAYER} Hubs`
        : noSlot
          ? 'No build slots open'
          : '';
    out.set(kind, { disabled, title });
  }
  return out;
}
